import { hashSeed, Rng } from '../math/rng';
import type { CaveShape, Look, PropRecipe, Pull, RockShape, V3, VoidShape } from './sdf';
import type { Clip } from './surfaceNets';

/**
 * The inside of a cave: a series of spaces between its two gates, generated from a seed.
 * Pure data (no three.js), in cave-local metres.
 *
 * Every space pulls you onto its walls (see Pull in sdf.ts): walls are floors, and the
 * middle of every space is weightless. The kinds:
 * - passage: a winding tube, 2.6–6 m across the radius, squeezing and widening;
 * - chamber: a room 8–14 m across (smaller in small caves);
 * - cavern: a big room with rock pillars through it (pillars pull too: walk up them);
 * - zeroG: a big room with no pull of its own: float across (floating boulders);
 * - grotto: a small room full of glowing crystals or fungi, often at a branch's end;
 * - crack: a tall, thin slot, pulling onto its two long walls, crossed by rock bridges;
 * - shaft: a wide tube with almost no pull, to drift along.
 */
export type SpaceKind = 'passage' | 'chamber' | 'cavern' | 'zeroG' | 'grotto' | 'crack' | 'shaft';

export interface SpaceInfo {
  kind: SpaceKind;
  /** Its void in `shape.voids`. */
  void: number;
  /** Centre of a room, midpoint of a tube (cave-local). */
  center: V3;
  /** Rough radius (m). */
  size: number;
  /** 0 at gate A … 1 at gate B. */
  along: number;
  /** On the way from gate A to gate B (false for branches and loops). */
  main: boolean;
}

export interface CaveGateSpec {
  /** The gate (cave-local). */
  p: V3;
  /** Level direction from the gate on into the cave. */
  dir: V3;
  /** "Down" at the gate (away from its world). */
  down: V3;
  /** The entrance's tube radius. */
  radius: number;
  /** Its world's surface gravity (m/s²). */
  g: number;
}

export interface InteriorSpec {
  seed: number;
  gates: [CaveGateSpec, CaveGateSpec];
  /** Every world near the cave, as its sea-level sphere (cave-local): the cave keeps out. */
  avoid: { c: V3; r: number }[];
  /** Tubes to keep clear of (the entrances' helices), cave-local. */
  avoidTubes: { points: V3[]; radius: number }[];
}

export interface CaveLayout {
  shape: CaveShape;
  spaces: SpaceInfo[];
  /** Centre-line from gate A to gate B through the main spaces, about 1 m apart. */
  route: V3[];
  /** The space each route point belongs to (index into `spaces`). */
  routeSpace: Int32Array;
}

/** Rock kept between the cave and any world's sea-level sphere, and between unrelated spaces (m). */
export const ROCK_MARGIN = 4;
const SPACE_GAP = 3;
/** Never narrower than this (m): the 1.8 m hero fits standing on any wall. */
export const MIN_RADIUS = 2.6;
/** The straight, level stretch after each gate before a passage starts to wander (m). */
const STUB = 8;
/** How far the cave's own tube runs back into each entrance (m): a clean joint. */
const BACK = 3;
/** Past a room's wall (m), the passages that leave it may still run together. */
const ROOM_MOUTH = 4;
/**
 * A passage bound for somewhere beside or behind a gate first heads this far on past the
 * stub, and this far to the side of the entrance, before it turns (m).
 */
const DETOUR_AHEAD = 10;
const DETOUR_ASIDE = 10;

/**
 * Caves pull gently, whatever the worlds either side: at most CAVE_G (0.5 g), at a passage's
 * wall. You drift more than walk, and a jump carries you across a passage to its far wall.
 */
export const CAVE_G = 0.5 * 9.81;
/** How hard each kind pulls at its wall, as a share of CAVE_G, and its profile. */
const PULL: Record<SpaceKind, { k: number; a: number }> = {
  passage: { k: 1, a: 0.4 },
  chamber: { k: 0.83, a: 1 },
  cavern: { k: 0.67, a: 1 },
  zeroG: { k: 0, a: 1 },
  grotto: { k: 0.9, a: 0.5 },
  crack: { k: 0.6, a: 0.4 },
  shaft: { k: 0.07, a: 1 },
};
/** Each kind's look: a tint for its stone and how wet it is (see caveInteriorMaterial). */
export const LOOK: Record<SpaceKind, Look> = {
  passage: [1, 0.95, 0.9, 0.25],
  shaft: [0.78, 0.8, 0.86, 0.45],
  chamber: [1.12, 1.07, 0.98, 0.35],
  cavern: [1.3, 1.24, 1.12, 0.2],
  zeroG: [1.2, 1.22, 1.32, 0.1],
  grotto: [0.95, 1, 1.1, 0.6],
  crack: [0.62, 0.62, 0.68, 0.55],
};
/** What grows in each kind of space (per square metre of wall); grottos add crystals or fungi. */
const PROPS: Record<SpaceKind, PropRecipe> = {
  passage: { stalagmites: 0.01, boulders: 0.008, crystals: 0.0012, fungi: 0.0012 },
  shaft: { boulders: 0.004, crystals: 0.004 },
  chamber: { stalagmites: 0.035, boulders: 0.02, crystals: 0.003, fungi: 0.003 },
  cavern: { stalagmites: 0.05, boulders: 0.03, crystals: 0.002 },
  zeroG: { boulders: 0.012, crystals: 0.004 },
  grotto: { stalagmites: 0.01 },
  crack: { stalagmites: 0.008, boulders: 0.02, fungi: 0.003 },
};
/** Colours of the light of crystals and of fungi. */
const CRYSTAL_GLOWS: [number, number, number][] = [
  [0.3, 0.95, 1.6],
  [0.95, 0.45, 1.7],
  [1.6, 0.95, 0.35],
  [0.45, 1.5, 0.75],
];
const FUNGUS_GLOWS: [number, number, number][] = [
  [0.25, 1.35, 1.0],
  [1.4, 0.4, 1.1],
  [0.85, 1.5, 0.25],
  [0.4, 0.65, 1.6],
];
const ROUGH: Record<SpaceKind, number> = {
  passage: 0.35,
  shaft: 0.3,
  chamber: 0.6,
  cavern: 0.9,
  zeroG: 0.7,
  grotto: 0.45,
  crack: 0.3,
};

/** A piece of cave for the clearance checks: spheres covering it. */
interface Blob {
  spheres: number[]; // x, y, z, r
  /** Pieces it may touch (it's joined to them). */
  friends: Set<number>;
}

/**
 * Lay out the inside of a cave. Deterministic for a given spec. Rooms that don't fit
 * are moved, shrunk or dropped, and optional pieces (branches, loops) are dropped if
 * they can't be placed, but gate A always connects to gate B.
 */
export function generateInterior(spec: InteriorSpec): CaveLayout {
  const [ga, gb] = spec.gates;
  const rng = new Rng(hashSeed(spec.seed, 0x6361));
  const span = sub(gb.p, ga.p);
  const D = len(span);
  const axis = scale(span, 1 / D);
  const small = D < 110;
  const large = D >= 300;
  const [u0, v0] = basis(axis);
  const alongOf = (p: V3) => clamp01(dot(sub(p, ga.p), axis) / D);

  const voids: VoidShape[] = [];
  const rock: RockShape[] = [];
  // Props draw from their own stream, so they never change the cave's shape.
  const propRng = new Rng(hashSeed(spec.seed, 0x7072));
  const caveGlow = propRng.pick(propRng.chance(0.5) ? CRYSTAL_GLOWS : FUNGUS_GLOWS);
  const propsFor = (kind: SpaceKind): PropRecipe => {
    const p = { ...PROPS[kind], glow: caveGlow };
    if (kind !== 'grotto') return p;
    // A grotto is either full of crystals or full of fungi, in a colour of its own.
    return propRng.chance(0.5)
      ? { ...p, crystals: 0.06, glow: propRng.pick(CRYSTAL_GLOWS) }
      : { ...p, fungi: 0.05, glow: propRng.pick(FUNGUS_GLOWS) };
  };
  const spaces: SpaceInfo[] = [];
  const blobs: Blob[] = [];
  const pullOf = (kind: SpaceKind): Pull => ({ s: PULL[kind].k * CAVE_G, a: PULL[kind].a });

  // --- Clearance ---------------------------------------------------------------------
  const inRock = (c: V3, r: number) => {
    for (const h of spec.avoid) if (dist(c, h.c) < h.r + ROCK_MARGIN + r) return false;
    return true;
  };
  /**
   * A sphere of a new piece must clear the entrances' tubes. At a gate the cave joins its
   * entrance, but only along the gate's axis (the stub, which runs inside the entrance) and
   * ahead of it: a piece that swung back beside or behind the gate would run into the
   * entrance, and its walls there are cut away with the stub's (gateClips), leaving a hole.
   */
  const clearOfEntrances = (c: V3, r: number, gates: CaveGateSpec[] = []) => {
    const onStub: CaveGateSpec[] = [];
    const ahead: CaveGateSpec[] = [];
    for (const g of gates) {
      const d = sub(c, g.p);
      const along = dot(d, g.dir);
      const aside = Math.sqrt(Math.max(0, dot(d, d) - along * along));
      if (aside < 0.5 && along >= -BACK - 0.5 && along <= STUB + 0.5) onStub.push(g);
      else if (along >= r) ahead.push(g);
    }
    for (const t of spec.avoidTubes) {
      // (Each entrance's path ends at its gate.)
      const end = t.points[t.points.length - 1];
      if (onStub.some((g) => dist(end, g.p) < 0.01)) continue;
      for (const q of t.points) {
        if (ahead.some((g) => dist(q, g.p) < STUB + 6)) continue;
        if (dist(c, q) < t.radius + r + SPACE_GAP) return false;
      }
    }
    return true;
  };
  /** Spheres of a new piece must clear other pieces, except inside `inside` (the rooms it leaves from). */
  const clearOfOthers = (spheres: number[], friends: Set<number>, inside: { c: V3; r: number }[] = []) => {
    for (let b = 0; b < blobs.length; b++) {
      if (friends.has(b)) continue;
      const o = blobs[b].spheres;
      for (let i = 0; i < spheres.length; i += 4) {
        const c: V3 = [spheres[i], spheres[i + 1], spheres[i + 2]];
        if (inside.some((z) => dist(c, z.c) < z.r)) continue;
        for (let j = 0; j < o.length; j += 4) {
          const d = Math.hypot(spheres[i] - o[j], spheres[i + 1] - o[j + 1], spheres[i + 2] - o[j + 2]);
          if (d < spheres[i + 3] + o[j + 3] + SPACE_GAP) return false;
        }
      }
    }
    return true;
  };
  const addBlob = (spheres: number[], friends: number[]) => {
    blobs.push({ spheres, friends: new Set(friends) });
    for (const f of friends) blobs[f].friends.add(blobs.length - 1);
    return blobs.length - 1;
  };

  // --- Rooms ---------------------------------------------------------------------------
  interface Room {
    kind: SpaceKind;
    c: V3;
    radii: V3;
    axes: [V3, V3, V3];
    blob: number;
    space: number;
  }
  const addRoom = (
    kind: SpaceKind,
    c: V3,
    radii: V3,
    axes: [V3, V3, V3],
    main: boolean,
    friends: number[],
  ): Room => {
    const t = alongOf(c);
    const size = Math.max(radii[0], radii[1], radii[2]);
    voids.push({
      kind: 'ellipsoid',
      center: c,
      axes,
      radii,
      rough: ROUGH[kind],
      look: LOOK[kind],
      pull: pullOf(kind),
      props: propsFor(kind),
    });
    spaces.push({ kind, void: voids.length - 1, center: c, size, along: t, main });
    // Cover the ellipsoid with spheres along its long axis (a little generous).
    const spheres: number[] = [];
    const long = radii.indexOf(size);
    const other = Math.max(...radii.filter((_, i) => i !== long));
    for (let s = -1; s <= 1; s += 0.5) {
      const p = add(c, scale(axes[long], s * (size - other)));
      spheres.push(p[0], p[1], p[2], other + ROUGH[kind]);
    }
    const blob = addBlob(spheres, friends);
    return { kind, c, radii, axes, blob, space: spaces.length - 1 };
  };
  /** Try to fit a room near `c`: nudged away from worlds, then smaller, then not at all. */
  const fitRoom = (kind: SpaceKind, c0: V3, r0: number, main: boolean, friends: number[]): Room | null => {
    for (let attempt = 0; attempt < 10; attempt++) {
      const r = r0 * (1 - 0.06 * attempt);
      let c: V3 = [...c0];
      // Push out of any world that's too close.
      for (const h of spec.avoid) {
        const d = dist(c, h.c);
        const need = h.r + ROCK_MARGIN + r + ROUGH[kind];
        if (d < need) c = add(h.c, scale(sub(c, h.c), need / d));
      }
      if (attempt > 0) c = add(c, scale(rng.unit(), attempt * 2));
      const radii = roomRadii(kind, r, rng);
      const axes = kind === 'crack' ? crackAxes(axis, rng) : randomAxes(rng);
      const size = Math.max(...radii) + ROUGH[kind];
      if (!inRock(c, size) || !clearOfEntrances(c, size, [ga, gb])) continue;
      if (!clearOfOthers([c[0], c[1], c[2], size], new Set(friends))) continue;
      return addRoom(kind, c, radii, axes, main, friends);
    }
    return null;
  };

  // --- Passages --------------------------------------------------------------------------
  /**
   * A tube from p0 to p1 leaving along t0 and arriving along t1, wandering sideways by up
   * to `wander` metres. Radii: `r0` at the ends' `endR` stretches, varying in between.
   */
  const addTube = (
    kind: SpaceKind,
    pts: V3[],
    radii: number[],
    main: boolean,
    friends: number[],
    gates: CaveGateSpec[],
    inside: { c: V3; r: number }[] = [],
  ): number | null => {
    const spheres: number[] = [];
    for (let i = 0; i < pts.length; i++) {
      const r = radii[i] + ROUGH[kind];
      if (!inRock(pts[i], r)) return null;
      if (!clearOfEntrances(pts[i], r, gates)) return null;
      spheres.push(pts[i][0], pts[i][1], pts[i][2], r);
    }
    if (!clearOfOthers(spheres, new Set(friends), inside)) return null;
    const mid = pts[Math.floor(pts.length / 2)];
    const t = alongOf(mid);
    voids.push({
      kind: 'tube',
      points: pts,
      radii,
      rough: ROUGH[kind],
      look: LOOK[kind],
      pull: pullOf(kind),
      props: propsFor(kind),
    });
    spaces.push({
      kind,
      void: voids.length - 1,
      center: mid,
      size: radii.reduce((a, b) => a + b, 0) / radii.length,
      along: t,
      main,
    });
    addBlob(spheres, friends);
    return spaces.length - 1;
  };
  const curve = (p0: V3, t0: V3, p1: V3, t1: V3, wander: number, side: V3, step = 2): V3[] => {
    const L = dist(p0, p1);
    const n = Math.max(2, Math.ceil(L / step));
    const out: V3[] = [];
    for (let i = 0; i <= n; i++) {
      const s = i / n;
      const h00 = 2 * s ** 3 - 3 * s ** 2 + 1;
      const h10 = s ** 3 - 2 * s ** 2 + s;
      const h01 = -2 * s ** 3 + 3 * s ** 2;
      const h11 = s ** 3 - s ** 2;
      let p = add(
        add(scale(p0, h00), scale(t0, h10 * L * 0.6)),
        add(scale(p1, h01), scale(t1, h11 * L * 0.6)),
      );
      p = add(p, scale(side, wander * Math.sin(Math.PI * s)));
      out.push(p);
    }
    return out;
  };
  const radiusProfile = (count: number, base: number, ends: [number, number], kind: SpaceKind): number[] => {
    const phase = rng.range(0, Math.PI * 2);
    const waves = rng.range(1, 3);
    const squeeze = kind === 'passage' && rng.chance(0.3) ? rng.range(0.25, 0.75) : -1;
    const swell = kind === 'passage' && rng.chance(0.3) ? rng.range(0.25, 0.75) : -1;
    const out: number[] = [];
    for (let i = 0; i < count; i++) {
      const s = count > 1 ? i / (count - 1) : 0;
      let r = base * (1 + 0.15 * Math.sin(phase + s * Math.PI * 2 * waves));
      if (squeeze >= 0) r = mix(r, MIN_RADIUS + 0.1, bump(s, squeeze, 0.08));
      if (swell >= 0) r = mix(r, base * 1.45, bump(s, swell, 0.1));
      // Ease to the end radii (the gates' tube, or a room's mouth).
      r = mix(r, ends[0], 1 - smooth(0, 0.18, s));
      r = mix(r, ends[1], smooth(0.82, 1, s));
      out.push(Math.max(MIN_RADIUS, Math.min(6, r)));
    }
    return out;
  };

  // --- The spine: gate A, rooms, gate B ----------------------------------------------------
  const roomCount = small
    ? D > 55 && rng.chance(0.6)
      ? 2
      : 1
    : large
      ? Math.min(7, Math.max(3, Math.round(D / 85)))
      : rng.int(2, 3);
  const mid = (roomCount - 1) / 2;
  const W = Math.min(0.3 * D, 120) * rng.range(0.35, 1);
  let heading = rng.range(0, Math.PI * 2);
  const rooms: Room[] = [];
  const kinds: SpaceKind[] = [];
  for (let i = 0; i < roomCount; i++) {
    if (small) kinds.push(rng.chance(0.6) ? 'chamber' : 'grotto');
    else if (Math.abs(i - mid) < 0.6) kinds.push(rng.chance(0.45) ? 'zeroG' : 'cavern');
    else {
      const roll = rng.float();
      kinds.push(
        roll < 0.45
          ? 'chamber'
          : roll < 0.65
            ? 'grotto'
            : roll < 0.85
              ? 'crack'
              : large
                ? 'cavern'
                : 'chamber',
      );
    }
  }
  // Gate stubs: the entrance tube carried straight on for a few metres.
  const stubEnd = (g: CaveGateSpec) => add(g.p, scale(g.dir, STUB));
  const endA = stubEnd(ga);
  const endB = stubEnd(gb);
  for (let i = 0; i < roomCount; i++) {
    const t = (i + 1) / (roomCount + 1) + rng.range(-0.06, 0.06) / (roomCount + 1);
    heading += rng.range(-1, 1);
    const side = add(scale(u0, Math.cos(heading)), scale(v0, Math.sin(heading)));
    const along = add(endA, scale(sub(endB, endA), t));
    const c = add(along, scale(side, W * Math.sin(Math.PI * t)));
    const kind = kinds[i];
    const r = roomSize(kind, small, rng);
    // Rooms mustn't swallow the gates.
    const room =
      fitRoom(
        kind,
        c,
        Math.max(4, Math.min(r, (dist(c, ga.p) - 12) * 0.8, (dist(c, gb.p) - 12) * 0.8)),
        true,
        [],
      ) ?? fitRoom('chamber', c, small ? 6 : 8, true, []);
    if (room) rooms.push(room);
  }

  // Passages along the spine.
  interface Node {
    p: V3;
    /** Direction a passage leaves along. */
    out: V3;
    r: number;
    room?: Room;
    /** A gate's node (the end of its stub). */
    gate?: CaveGateSpec;
  }
  const gateNode = (g: CaveGateSpec, e: V3): Node => ({ p: e, out: g.dir, r: g.radius, gate: g });
  /**
   * Where a passage between gate node `n` and `target` turns: out ahead of the gate and round
   * to the target's side, when the target lies beside or behind the gate (null when it's well
   * ahead). Heading straight for it would swing the passage back past the gate, into its
   * entrance.
   */
  const detour = (n: Node, target: V3): V3 | null => {
    const g = n.gate;
    if (!g) return null;
    const rel = sub(target, g.p);
    const ahead = dot(rel, g.dir);
    if (ahead >= STUB + DETOUR_AHEAD / 2) return null;
    const lat = sub(rel, scale(g.dir, ahead));
    const side = len(lat) > 1e-3 ? norm(lat) : basis(g.dir)[0];
    return add(add(g.p, scale(g.dir, STUB + DETOUR_AHEAD)), scale(side, g.radius + DETOUR_ASIDE));
  };
  /** The points a spine passage runs through: its two nodes, and a detour at either gate. */
  const waypoints = (from: Node, to: Node): V3[] => {
    const a = detour(from, to.p);
    const b = detour(to, a ?? from.p);
    return [from.p, ...(a ? [a] : []), ...(b ? [b] : []), to.p];
  };
  /** A smooth path through `way`, leaving along t0 and arriving along t1. */
  const path = (way: V3[], t0: V3, t1: V3, wander: number, side: V3): V3[] => {
    if (way.length === 2) return curve(way[0], t0, way[1], t1, wander, side);
    let L = 0;
    for (let k = 1; k < way.length; k++) L += dist(way[k - 1], way[k]);
    const out: V3[] = [];
    for (let k = 0; k + 1 < way.length; k++) {
      // Through each turn, heading from the point before it to the point after.
      const ta = k === 0 ? t0 : norm(sub(way[k + 1], way[k - 1]));
      const tb = k + 2 === way.length ? t1 : norm(sub(way[k + 2], way[k]));
      const seg = curve(way[k], ta, way[k + 1], tb, (wander * dist(way[k], way[k + 1])) / L, side);
      out.push(...(k === 0 ? seg : seg.slice(1)));
    }
    return out;
  };
  const nodes: Node[] = [
    gateNode(ga, endA),
    ...rooms.map((room) => ({ p: room.c, out: axis, r: 0, room })),
    gateNode(gb, endB),
  ];
  const mainTubes: number[] = [];
  const addSpine = (from: Node, to: Node, kind: SpaceKind): number | null => {
    const way = waypoints(from, to);
    const dir = norm(sub(way[1], way[0]));
    const dirIn = norm(sub(way[way.length - 1], way[way.length - 2]));
    const t0 = from.room ? norm(add(dir, scale(rng.unit(), 0.35))) : from.out;
    const t1 = to.room ? norm(add(dirIn, scale(rng.unit(), 0.35))) : scale(to.out, -1);
    const L = dist(from.p, to.p);
    const [bu, bv] = basis(dir);
    const a = rng.range(0, Math.PI * 2);
    const side = add(scale(bu, Math.cos(a)), scale(bv, Math.sin(a)));
    for (let attempt = 0; attempt < 8; attempt++) {
      const wander = (kind === 'shaft' ? 0.03 : 0.15) * L * (1 - attempt / 8);
      const pts = withStubs(from, to, path(way, t0, t1, wander, side));
      const base = kind === 'shaft' ? rng.range(5, 6.5) : small ? rng.range(2.8, 3.4) : rng.range(3.2, 4.2);
      const endR = (n: Node) => (n.room ? Math.min(base * 1.3, 5) : n.r);
      const radii = radiusProfile(pts.length, base, [endR(from), endR(to)], kind);
      // Hold the entrance's radius exactly along the stubs.
      pinStub(pts, radii, from, to);
      const friends = [from.room?.blob, to.room?.blob].filter((b): b is number => b !== undefined);
      // In and just outside the rooms it joins it may meet the passages that join them too
      // (they all start from a room's middle, and two leaving one room make one wide mouth).
      const inside = [from.room, to.room]
        .filter((r): r is Room => r !== undefined)
        .map((r) => ({ c: r.c, r: Math.max(...r.radii) + ROOM_MOUTH }));
      const id = addTube(kind, pts, radii, true, friends, [ga, gb], inside);
      if (id !== null) return id;
    }
    return null;
  };
  const shaftAt = large && rng.chance(0.6) ? rng.int(1, nodes.length - 3) : -1;
  for (let i = 0; i + 1 < nodes.length; i++) {
    const id = addSpine(nodes[i], nodes[i + 1], i === shaftAt ? 'shaft' : 'passage');
    if (id !== null) mainTubes.push(id);
    else {
      // Drop the room we couldn't reach and join straight on (rare).
      const straight = addStraight(nodes[i], nodes[i + 1]);
      if (straight !== null) mainTubes.push(straight);
    }
  }
  /** The gate ends of a spine passage: back into the entrance, the gate, then the curve. */
  function withStubs(from: Node, to: Node, pts: V3[]): V3[] {
    const head = from.room
      ? []
      : [sub(from.p, scale(from.out, STUB + BACK)), sub(from.p, scale(from.out, STUB))];
    const tail = to.room ? [] : [sub(to.p, scale(to.out, STUB)), sub(to.p, scale(to.out, STUB + BACK))];
    return [...head, ...pts, ...tail];
  }
  function addStraight(from: Node, to: Node): number | null {
    // Straight lines, round a gate if one's in the way.
    const way = waypoints(from, to);
    const line: V3[] = [];
    for (let k = 0; k + 1 < way.length; k++) {
      const d = norm(sub(way[k + 1], way[k]));
      const seg = curve(way[k], d, way[k + 1], d, 0, [0, 0, 0]);
      line.push(...(k === 0 ? seg : seg.slice(1)));
    }
    const pts = withStubs(from, to, line);
    const radii = pts.map(() => Math.max(from.r || 3.2, to.r || 3.2));
    pinStub(pts, radii, from, to);
    // Forced: the cave must connect, so skip the checks against other pieces.
    voids.push({
      kind: 'tube',
      points: pts,
      radii,
      rough: ROUGH.passage,
      look: LOOK.passage,
      pull: pullOf('passage'),
      props: propsFor('passage'),
    });
    spaces.push({
      kind: 'passage',
      void: voids.length - 1,
      center: pts[pts.length >> 1],
      size: radii[0],
      along: 0.5,
      main: true,
    });
    blobs.push({ spheres: [], friends: new Set() });
    return spaces.length - 1;
  }
  function pinStub(pts: V3[], radii: number[], from: Node, to: Node): void {
    for (let i = 0; i < pts.length; i++) {
      if (
        !from.room &&
        dist(pts[i], from.p) <= STUB + BACK + 0.01 &&
        dot(sub(pts[i], from.p), from.out) <= 0.01
      )
        radii[i] = from.r;
      if (!to.room && dist(pts[i], to.p) <= STUB + BACK + 0.01 && dot(sub(pts[i], to.p), to.out) <= 0.01)
        radii[i] = to.r;
    }
  }

  // --- Branches and loops ------------------------------------------------------------------
  const branchCount = small ? rng.int(0, 1) : large ? rng.int(2, 3) : rng.int(1, 2);
  for (let b = 0; b < branchCount && rooms.length > 0; b++) {
    const root = rooms[rng.int(0, rooms.length - 1)];
    for (let attempt = 0; attempt < 8; attempt++) {
      const u = rng.unit();
      const dir = norm(sub(u, scale(axis, dot(u, axis))));
      const length = small ? rng.range(10, 18) : rng.range(15, 40);
      const rootR = Math.max(...root.radii);
      const start = root.c;
      const endP = add(root.c, scale(dir, rootR + length));
      const grottoR = small ? rng.range(4, 6) : rng.range(5, 8);
      const g = fitRoom('grotto', add(endP, scale(dir, grottoR)), grottoR, false, []);
      if (!g) continue;
      const pts = curve(start, dir, g.c, dir, 0.12 * length, rng.unit());
      const radii = radiusProfile(
        pts.length,
        small ? 2.9 : 3.3,
        [Math.min(4, rootR), Math.min(3.6, grottoR)],
        'passage',
      );
      const leaving = [{ c: root.c, r: rootR + 1 }];
      if (addTube('passage', pts, radii, false, [root.blob, g.blob], [ga, gb], leaving) !== null) break;
      // Couldn't reach it: take the grotto back out.
      removeLast(1);
    }
  }
  if (large && rooms.length >= 3 && rng.chance(0.5)) {
    const i = rng.int(0, rooms.length - 3);
    const [r0, r2] = [rooms[i], rooms[i + 2]];
    const dir = norm(sub(r2.c, r0.c));
    const [bu, bv] = basis(dir);
    const a = rng.range(0, Math.PI * 2);
    const side = add(scale(bu, Math.cos(a)), scale(bv, Math.sin(a)));
    const pts = curve(r0.c, dir, r2.c, dir, rng.range(18, 32), side);
    const radii = radiusProfile(pts.length, rng.range(3, 3.8), [3.5, 3.5], 'passage');
    const ends = [r0, r2].map((r) => ({ c: r.c, r: Math.max(...r.radii) + 1 }));
    addTube('passage', pts, radii, false, [r0.blob, r2.blob, rooms[i + 1].blob], [ga, gb], ends);
  }
  function removeLast(n: number): void {
    for (let k = 0; k < n; k++) {
      voids.pop();
      spaces.pop();
      const gone = blobs.length - 1;
      for (const f of blobs[gone].friends) blobs[f]?.friends.delete(gone);
      blobs.pop();
    }
  }

  // --- The route ------------------------------------------------------------------------------
  const route: V3[] = [];
  const routeSpace: number[] = [];
  const spineSpaces = [...mainTubes];
  // Walk the spine: passage, room, passage, ... in order from gate A.
  const push = (p: V3, s: number) => {
    const last = route[route.length - 1];
    if (last && dist(last, p) < 1e-6) return;
    // Resample to about 1 m.
    if (last) {
      const n = Math.floor(dist(last, p) / 1);
      for (let k = 1; k <= n; k++) {
        route.push(lerp(last, p, k / (n + 1)));
        routeSpace.push(s);
      }
    }
    route.push(p);
    routeSpace.push(s);
  };
  push(ga.p, spineSpaces[0] ?? 0);
  // (Skipping the few points that run back into each entrance behind its gate.)
  const behindGate = (p: V3) =>
    [ga, gb].some((g) => dist(p, g.p) <= BACK + 0.01 && dot(sub(p, g.p), g.dir) < -0.01);
  spineSpaces.forEach((sp) => {
    const v = voids[spaces[sp].void];
    if (v.kind !== 'tube') return;
    for (const p of v.points) if (!behindGate(p)) push(p, sp);
  });
  push(gb.p, spineSpaces[spineSpaces.length - 1] ?? 0);
  // Rooms own the route points inside them.
  rooms.forEach((room) => {
    for (let i = 0; i < route.length; i++) {
      if (insideEllipsoid(route[i], room.c, room.axes, room.radii)) routeSpace[i] = room.space;
    }
  });

  // Pillars in caverns, bridges across cracks: rock put back, clear of the way through.
  const blocksRoute = (a: V3, b: V3, r: number) => {
    const ab = sub(b, a);
    const l2 = dot(ab, ab);
    for (const p of route) {
      const t = clamp01(dot(sub(p, a), ab) / l2);
      if (dist(p, add(a, scale(ab, t))) < r + 1.8) return true;
    }
    return false;
  };
  const addRock = (make: () => RockShape) => {
    for (let attempt = 0; attempt < 12; attempt++) {
      const piece = make();
      if (!blocksRoute(piece.a, piece.b, piece.radius)) {
        rock.push(piece);
        return;
      }
    }
  };
  for (const room of rooms) {
    if (room.kind === 'cavern') {
      const count = rng.int(1, 3);
      for (let k = 0; k < count; k++) {
        addRock(() => {
          const dir = rng.unit();
          const off = scale(rng.unit(), rng.range(0.15, 0.45) * Math.min(...room.radii));
          const reach = Math.max(...room.radii) * 1.1;
          const c = add(room.c, off);
          return { a: add(c, scale(dir, -reach)), b: add(c, scale(dir, reach)), radius: rng.range(3, 3.8) };
        });
      }
    } else if (room.kind === 'crack') {
      const count = rng.int(1, 2);
      const across = room.axes[2]; // the thin axis
      for (let k = 0; k < count; k++) {
        addRock(() => {
          const along = scale(room.axes[0], rng.range(-0.55, 0.55) * room.radii[0]);
          const up = scale(room.axes[1], rng.range(-0.45, 0.45) * room.radii[1]);
          const c = add(add(room.c, along), up);
          const w = room.radii[2] + 2;
          return { a: add(c, scale(across, -w)), b: add(c, scale(across, w)), radius: rng.range(2.2, 3) };
        });
      }
    } else if (room.kind === 'zeroG') {
      // Boulders adrift in the middle, clear of the way through: rock to push off from.
      const count = propRng.int(5, 9);
      for (let k = 0; k < count; k++) {
        addRock(() => {
          const r = propRng.range(1.2, 2.6);
          const room0 = Math.min(...room.radii) - r - 2;
          const c = add(room.c, scale(propRng.unit(), propRng.range(0.15, 0.8) * room0));
          const stretch = scale(propRng.unit(), propRng.range(0, 0.7) * r);
          return { a: add(c, stretch), b: sub(c, stretch), radius: r };
        });
      }
    }
  }

  return {
    shape: {
      voids,
      rock,
      blend: small ? 2.5 : 3,
      seed: hashSeed(spec.seed, 0x7368),
      gates: [{ p: ga.p }, { p: gb.p }],
    },
    spaces,
    route,
    routeSpace: Int32Array.from(routeSpace),
  };
}

function roomSize(kind: SpaceKind, small: boolean, rng: Rng): number {
  switch (kind) {
    case 'cavern':
      return rng.range(18, 25);
    case 'zeroG':
      return rng.range(15, 21);
    case 'crack':
      return rng.range(20, 28);
    case 'grotto':
      return small ? rng.range(4.5, 6.5) : rng.range(5, 8);
    default:
      return small ? rng.range(6, 9) : rng.range(8, 13);
  }
}

function roomRadii(kind: SpaceKind, r: number, rng: Rng): V3 {
  if (kind === 'crack') return [r, r * rng.range(0.55, 0.7), rng.range(4.5, 6)];
  if (kind === 'zeroG') return [r, r * rng.range(0.85, 1), r * rng.range(0.8, 0.95)];
  return [r, r * rng.range(0.7, 1), r * rng.range(0.6, 0.9)];
}

function randomAxes(rng: Rng): [V3, V3, V3] {
  const a = rng.unit();
  const [b, c] = basis(a);
  return [a, b, c];
}

/** A crack runs roughly along the cave, its thin axis sideways. */
function crackAxes(axis: V3, rng: Rng): [V3, V3, V3] {
  const long = norm(add(axis, scale(rng.unit(), 0.4)));
  const [b, c] = basis(long);
  const a = rng.range(0, Math.PI * 2);
  const tall = add(scale(b, Math.cos(a)), scale(c, Math.sin(a)));
  return [long, tall, cross(long, tall)];
}

function insideEllipsoid(p: V3, c: V3, axes: [V3, V3, V3], r: V3): boolean {
  const d = sub(p, c);
  return (dot(d, axes[0]) / r[0]) ** 2 + (dot(d, axes[1]) / r[1]) ** 2 + (dot(d, axes[2]) / r[2]) ** 2 < 1;
}

// --- Small vector helpers (V3 tuples) ------------------------------------------------------------
function add(a: V3, b: V3): V3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
function sub(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function scale(a: V3, s: number): V3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}
function dot(a: V3, b: V3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function len(a: V3): number {
  return Math.hypot(a[0], a[1], a[2]);
}
function norm(a: V3): V3 {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}
function dist(a: V3, b: V3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
function lerp(a: V3, b: V3, t: number): V3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
function basis(d: V3): [V3, V3] {
  const helper: V3 = Math.abs(d[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = norm(cross(helper, d));
  return [u, cross(d, u)];
}
function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function smooth(e0: number, e1: number, x: number): number {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
/** 1 at `at`, falling to 0 over `width` either side. */
function bump(s: number, at: number, width: number): number {
  return 1 - smooth(0, width, Math.abs(s - at));
}

/**
 * Where the interior's walls are cut at its gates (see Clip): its stub back into each
 * entrance (the gate's tube radius, give or take the walls' bumps and the mesh's cells) out
 * to the stub's rounded end. Only there: the entrance's own walls take over.
 */
export function gateClips(spec: InteriorSpec): Clip[] {
  return spec.gates.map((g) => ({
    p: g.p,
    n: g.dir,
    radius: g.radius + CLIP_MARGIN,
    depth: BACK + g.radius + CLIP_MARGIN,
  }));
}
const CLIP_MARGIN = 1.2;

/**
 * The direction a cave's rock layers are stacked along: across the line between its
 * gates (so the layers run along the cave), turned by its seed.
 */
export function strataOf(spec: InteriorSpec): V3 {
  const axis = norm(sub(spec.gates[1].p, spec.gates[0].p));
  const u = new Rng(hashSeed(spec.seed, 0x5745)).unit();
  const s = sub(u, scale(axis, dot(u, axis)));
  return len(s) > 1e-6 ? norm(s) : basis(axis)[0];
}
